import { supabase } from './supabaseClient';
import { defaultBrandPresets } from '../config/brandPresets';

class BrandPresetStorage {
  _toApp(row) {
    return {
      id: row.id,
      name: row.name,
      domain: row.domain || '',
      primaryColor: row.primary_color,
      secondaryColor: row.secondary_color,
      backgroundColor: row.background_color,
      textColor: row.text_color,
      logoUrl: row.logo_url || '',
      layout: row.layout,
      isDefault: row.is_default,
      isCustom: row.is_custom,
      isEditable: row.is_editable,
      isArchived: row.is_archived,
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  _toDb(data) {
    const row = {};
    if (data.id !== undefined) row.id = data.id;
    if (data.name !== undefined) row.name = data.name;
    if (data.domain !== undefined) row.domain = data.domain;
    if (data.primaryColor !== undefined) row.primary_color = data.primaryColor;
    if (data.secondaryColor !== undefined) row.secondary_color = data.secondaryColor;
    if (data.backgroundColor !== undefined) row.background_color = data.backgroundColor;
    if (data.textColor !== undefined) row.text_color = data.textColor;
    if (data.logoUrl !== undefined) row.logo_url = data.logoUrl;
    if (data.layout !== undefined) row.layout = data.layout;
    if (data.isDefault !== undefined) row.is_default = data.isDefault;
    if (data.isCustom !== undefined) row.is_custom = data.isCustom;
    if (data.isEditable !== undefined) row.is_editable = data.isEditable;
    if (data.isArchived !== undefined) row.is_archived = data.isArchived;
    return row;
  }

  async getAllCustomPresets() {
    const { data, error } = await supabase
      .from('brand_presets')
      .select('*')
      .eq('is_custom', true)
      .eq('is_archived', false);

    if (error) throw error;
    return (data || []).map(row => this._toApp(row));
  }

  async getArchivedPresets() {
    const { data, error } = await supabase
      .from('brand_presets')
      .select('*')
      .eq('is_archived', true);

    if (error) throw error;
    return (data || []).map(row => this._toApp(row));
  }

  async getEditedDefaults() {
    const { data, error } = await supabase
      .from('brand_presets')
      .select('*')
      .eq('is_default', true)
      .eq('is_archived', false);

    if (error) throw error;

    const edited = {};
    (data || []).forEach(row => {
      edited[row.id] = this._toApp(row);
    });
    return edited;
  }

  async createPreset(presetData) {
    const id = presetData.id || presetData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const row = {
      ...this._toDb(presetData),
      id,
      is_custom: true,
      is_archived: false
    };

    const { data, error } = await supabase
      .from('brand_presets')
      .insert(row)
      .select()
      .single();

    if (error) throw error;
    return this._toApp(data);
  }

  async updatePreset(id, presetData) {
    // Check if it's a default preset being edited
    if (defaultBrandPresets[id]) {
      const row = {
        ...this._toDb(presetData),
        id,
        is_default: true,
        is_editable: true,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('brand_presets')
        .upsert(row)
        .select()
        .single();

      if (error) throw error;
      return this._toApp(data);
    }

    // Custom preset
    const row = {
      ...this._toDb(presetData),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('brand_presets')
      .update(row)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return this._toApp(data);
  }

  async archivePreset(id) {
    // If it's a default that hasn't been saved to DB yet, insert it
    if (defaultBrandPresets[id]) {
      const preset = defaultBrandPresets[id];
      const row = {
        ...this._toDb(preset),
        id,
        is_default: true,
        is_editable: true,
        is_archived: true,
        archived_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('brand_presets')
        .upsert(row);

      if (error) throw error;
      return;
    }

    const { error } = await supabase
      .from('brand_presets')
      .update({ is_archived: true, archived_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  }

  async unarchivePreset(id) {
    const { error } = await supabase
      .from('brand_presets')
      .update({ is_archived: false, archived_at: null })
      .eq('id', id);

    if (error) throw error;
  }

  async deletePreset(id) {
    const { error } = await supabase
      .from('brand_presets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

export const brandPresetStorage = new BrandPresetStorage();
